/**
 * Derives environment type from the current hostname (for use in browser).
 * Prod -> admin.atlassian.com; staging/dev/other -> admin.stg.atlassian.com (org path appended when building URLs).
 */
export function getEnvTypeFromHostname(hostname: string): 'dev' | 'staging' | 'prod' {
	if (typeof hostname !== 'string') {
		return 'prod';
	}
	const h = hostname.toLowerCase();
	if (h.includes('dev.') || h.includes('.dev')) {
		return 'dev';
	}
	if (h.includes('stg') || h.includes('staging')) {
		return 'staging';
	}
	return 'prod';
}

/**
 * Base admin URL with no org path. Used when orgId is not available (message requires orgId to hydrate; fallback for CTA).
 * Prod: https://admin.atlassian.com; anything else (staging/dev): https://admin.stg.atlassian.com
 */
export function getAdminBaseUrl(envType: string): string {
	return envType === 'prod' ? 'https://admin.atlassian.com' : 'https://admin.stg.atlassian.com';
}

/**
 * Builds the org-level Rovo admin hub URL (same as Confluence Rovo onboarding CTA).
 * Used for Jira side-nav and Confluence side-nav so both link to the same Rovo setup experience.
 * Prod: https://admin.atlassian.com/o/{orgId}/rovo; anything else (staging/dev): https://admin.stg.atlassian.com/o/{orgId}/rovo
 */
export function getRovoOnboardingAdminUrl(orgId: string, envType: string): string {
	const base =
		envType === 'prod' ? 'https://admin.atlassian.com' : 'https://admin.stg.atlassian.com';
	return `${base}/o/${orgId}/rovo`;
}

/**
 * URL for the "Get started" CTA. When orgId is present (required for message to hydrate), returns the org Rovo page; otherwise the plain admin base URL.
 */
export function getRovoOnboardingClickUrl(orgId: string | undefined, envType: string): string {
	if (orgId && orgId.trim() !== '') {
		return getRovoOnboardingAdminUrl(orgId, envType);
	}
	return getAdminBaseUrl(envType);
}
